import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { ArticleBody } from "~/components/cih/article-body";
import {
  ArticleAuthorAvatar,
  ArticleAuthorProfileButton,
} from "~/components/cih/article-coach-links";
import { CihArticleReader } from "~/components/cih/article-reader";
import {
  CihArticleRows,
  type ArticleRowData,
} from "~/components/cih/article-rows";
import { CihClosingCta } from "~/components/cih/closing-cta";
import { RelatedEntityPills } from "~/components/cih/related-entity-pills";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { Button } from "~/components/ui/button";
import {
  getMoreArticlesByChallengeFn,
  getMoreArticlesByCoachFn,
  getPublishedArticleBySlugFn,
  type ArticleCoachCco,
  type ArticleRelatedChallenge,
  type MoreArticleRow,
} from "~/lib/functions/cih/articles";
import {
  createArticleStructuredData,
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

type LoaderData = {
  title: string;
  article: NonNullable<
    Awaited<ReturnType<typeof getPublishedArticleBySlugFn>>
  >["article"];
  coach: NonNullable<
    Awaited<ReturnType<typeof getPublishedArticleBySlugFn>>
  >["coach"];
  challenge: NonNullable<
    Awaited<ReturnType<typeof getPublishedArticleBySlugFn>>
  >["challenge"];
  coachCcos: ArticleCoachCco[];
  relatedChallenges: ArticleRelatedChallenge[];
  moreByCoach: MoreArticleRow[];
  moreOnChallenge: MoreArticleRow[];
  publishedAtFormatted: string | null;
  publishedAtIso: string | null;
};

export const Route = createFileRoute("/_public/articles/$slug")({
  staticData: { title: "Article" },
  loader: async ({ params }): Promise<LoaderData> => {
    const data = await getPublishedArticleBySlugFn({
      data: { slug: params.slug },
    });
    if (!data) throw notFound();
    const coachId = data.coach?.id;
    const [moreByCoach, moreOnChallenge] = await Promise.all([
      coachId
        ? getMoreArticlesByCoachFn({
            data: { coachId, excludeArticleId: data.article.id },
          })
        : Promise.resolve<MoreArticleRow[]>([]),
      getMoreArticlesByChallengeFn({
        data: {
          challengeId: data.challenge.id,
          excludeArticleId: data.article.id,
        },
      }),
    ]);
    const publishedDate = data.article.publishedAt
      ? new Date(data.article.publishedAt)
      : null;
    return {
      title: data.article.title,
      ...data,
      moreByCoach,
      moreOnChallenge,
      publishedAtFormatted: publishedDate
        ? publishedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
          })
        : null,
      publishedAtIso: publishedDate ? publishedDate.toISOString() : null,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const url = `https://dreamcoachmatch.com/articles/${params.slug}`;
    const base = createBrandMeta({
      title: loaderData?.article.title ?? params.slug,
      description:
        loaderData?.article.description ||
        "Article from a Dream Coach Match coach.",
      ogUrl: url,
      ogType: "article",
      canonical: url,
      author:
        loaderData?.coach?.fullName ??
        loaderData?.article.authorName ??
        undefined,
      publishedTime: loaderData?.publishedAtIso ?? undefined,
    });
    if (!loaderData) return base;

    const articleLd = createArticleStructuredData({
      headline: loaderData.article.title,
      description: loaderData.article.description ?? undefined,
      author:
        loaderData.coach?.fullName ??
        loaderData.article.authorName ??
        undefined,
      publishedDate: loaderData.publishedAtIso ?? undefined,
      url,
    });
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Articles", url: "https://dreamcoachmatch.com/articles" },
      { name: loaderData.article.title, url },
    ]);

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const {
    article,
    coach,
    challenge,
    coachCcos,
    relatedChallenges,
    moreByCoach,
    moreOnChallenge,
    publishedAtFormatted,
  } = Route.useLoaderData();

  const toArticleRows = (rows: MoreArticleRow[]): ArticleRowData[] =>
    rows.map((a) => ({
      id: a.id,
      slug: a.slug,
      title: a.title,
      description: a.description,
      authorName: a.coachFullName ?? a.authorName ?? "",
      challengeTitle: a.challengeTitle,
      challengeSlug: a.challengeSlug,
    }));

  const credentialString =
    coachCcos.length > 0 ? coachCcos.map((c) => c.name).join(" · ") : null;

  const relatedChallengePills = relatedChallenges
    .filter((c): c is ArticleRelatedChallenge & { slug: string } =>
      Boolean(c.slug),
    )
    .map((c) => ({ label: c.title, href: `/coaching-for/${c.slug}` }));

  return (
    <>
      <CihArticleReader
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Articles", href: "/articles" },
          { label: article.title, href: `/articles/${article.slug}` },
        ]}
        kicker={`Article · ${challenge.title}`}
        title={article.title}
        description={article.description}
        author={
          coach
            ? {
                kind: "coach",
                username: coach.username,
                hasPublicProfile: coach.hasPublicProfile,
                fullName: coach.fullName,
                profilePictureUrl: coach.profilePictureUrl,
                credential: credentialString,
              }
            : {
                kind: "freeText",
                name: article.authorName ?? "",
                title: article.authorTitle,
              }
        }
        metaStrip={[
          {
            label: "Challenge",
            entries: [
              {
                text: challenge.title,
                href: challenge.slug
                  ? `/coaching-for/${challenge.slug}`
                  : undefined,
              },
            ],
          },
          ...(coachCcos.length > 0
            ? [
                {
                  label: "Credentialed by",
                  entries: coachCcos.map((c) => ({
                    text: c.name,
                    href: c.slug ? `/organizations/${c.slug}` : undefined,
                  })),
                },
              ]
            : []),
        ]}
        publishedAt={publishedAtFormatted ?? undefined}
      >
        <ArticleBody body={article.body} ugc />
      </CihArticleReader>

      {coach && moreByCoach.length > 0 ? (
        <CihSection>
          <CihArticleRows
            articles={toArticleRows(moreByCoach)}
            sectionLabel="Keep reading"
            heading={`More from ${coach.fullName}`}
            showChallenge
          />
        </CihSection>
      ) : null}

      {moreOnChallenge.length > 0 ? (
        <CihSection>
          <CihArticleRows
            articles={toArticleRows(moreOnChallenge)}
            sectionLabel="Keep reading"
            heading={`More on ${challenge.title}`}
          />
        </CihSection>
      ) : null}

      <CihSection>
        <CihSectionHeader
          label="Explore"
          title={`More on ${challenge.title}`}
        />
        <div className="mx-auto max-w-7xl px-4 pb-6 xl:px-0">
          <p className="max-w-2xl text-base leading-relaxed text-gray-700">
            {challenge.description ||
              `Browse the full guide and every coach who specializes in ${challenge.title.toLowerCase()}.`}
          </p>
          {challenge.slug ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/coaching-for/$challengeSlug"
                params={{ challengeSlug: challenge.slug }}
              >
                <Button variant="primary" size="lg">
                  Read the full guide →
                </Button>
              </Link>
              <Link
                to="/coaching-for/$challengeSlug/coaches"
                params={{ challengeSlug: challenge.slug }}
              >
                <Button variant="default" size="lg">
                  See coaches for this challenge
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
        {relatedChallengePills.length > 0 ? (
          <RelatedEntityPills
            items={relatedChallengePills}
            sectionLabel="Related"
            heading="Related challenges"
          />
        ) : null}
      </CihSection>

      {coach ? (
        <CihSection>
          <CihSectionHeader label="About the author" title={coach.fullName} />
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 pb-6 md:grid-cols-[auto_1fr] md:gap-12 xl:px-0">
            <ArticleAuthorAvatar coach={coach} />
            <div>
              {coachCcos.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {coachCcos.map((c) =>
                    c.slug ? (
                      <Link
                        key={c.id}
                        to="/organizations/$slug"
                        params={{ slug: c.slug }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-3.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-purple-400 hover:text-purple-700"
                      >
                        {c.name} <span className="text-gray-400">→</span>
                      </Link>
                    ) : (
                      <span
                        key={c.id}
                        className="inline-flex items-center rounded-full border border-gray-300 px-3.5 py-1.5 text-xs font-medium text-gray-700"
                      >
                        {c.name}
                      </span>
                    ),
                  )}
                </div>
              ) : null}
              <ArticleAuthorProfileButton coach={coach} />
            </div>
          </div>
        </CihSection>
      ) : null}

      <CihClosingCta
        headline="Your next chapter starts with a question."
        sub="Free · 5 minutes · No commitment"
        cta={
          <Link to="/intake">
            <Button variant="primary" size="lg">
              Start the intake →
            </Button>
          </Link>
        }
      />
    </>
  );
}
